/**
 * QueryState — loading, failed, empty or data, from one TanStack query.
 *
 * The job is to make "the request failed" and "there is nothing" look
 * different. Five matter tabs said "No … filed yet" on a 500 before this.
 *
 *     <QueryState
 *       query={matters}
 *       resource="matters"
 *       isEmpty={(d) => d.items.length === 0}
 *       empty={<EmptyState title="No matters yet" />}
 *     >
 *       {(data) => <MatterList rows={data.items} />}
 *     </QueryState>
 *
 * - Loading (no data yet) → `skeleton` (default: three rows).
 * - Error with no data → ErrorState (402/403/404 variants, Retry for the rest).
 * - Error while stale data is on screen (a refetch failed) → the data, with a
 *   compact ErrorState above it. The rows are still true; hiding them is not.
 * - Data and `isEmpty(data)` → `empty`.
 * - Otherwise → `children(data)`.
 */
import type { ReactNode } from 'react';

import { cn } from './cn';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Skeleton } from './Skeleton';

/** The slice of a UseQueryResult this needs — any TanStack query satisfies it. */
export interface QueryLike<T> {
  data: T | undefined;
  error: unknown;
  isError: boolean;
  isLoading?: boolean;
  isPending?: boolean;
  refetch?: () => unknown;
}

export interface QueryStateProps<T> {
  query: QueryLike<T>;
  children: (data: T) => ReactNode;
  /** True when the loaded data has nothing to show. Default: empty array / null. */
  isEmpty?: (data: T) => boolean;
  /** What to render for an empty answer. Default: a plain EmptyState. */
  empty?: ReactNode;
  /** What failed, for the error sentence ("matters", "this matter"). */
  resource?: string;
  skeleton?: ReactNode;
  /** Hide the Retry button. */
  noRetry?: boolean;
  className?: string;
}

function defaultIsEmpty(data: unknown): boolean {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

export function QueryState<T>({
  query,
  children,
  isEmpty = defaultIsEmpty,
  empty,
  resource,
  skeleton,
  noRetry = false,
  className,
}: QueryStateProps<T>) {
  const retry = !noRetry && query.refetch ? () => void query.refetch?.() : undefined;
  const loading = query.data === undefined && !query.isError && (query.isLoading ?? query.isPending ?? true);

  if (loading) {
    return (
      <div aria-busy="true" className={className}>
        {skeleton ?? (
          <div className="space-y-2">
            <Skeleton className="h-12 rounded-lg" />
            <Skeleton className="h-12 rounded-lg" />
            <Skeleton className="h-12 rounded-lg" />
          </div>
        )}
      </div>
    );
  }

  if (query.isError && query.data === undefined) {
    return <ErrorState className={className} error={query.error} resource={resource} onRetry={retry} />;
  }

  const data = query.data as T;
  const body = isEmpty(data)
    ? (empty ?? <EmptyState title={resource ? `No ${resource} yet` : 'Nothing here yet'} />)
    : children(data);

  if (query.isError) {
    return (
      <div className={cn('space-y-3', className)}>
        <ErrorState compact error={query.error} resource={resource} onRetry={retry} />
        {body}
      </div>
    );
  }
  return className ? <div className={className}>{body}</div> : <>{body}</>;
}
